import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * Public join page — Azubi clicks QR/link and joins a trainer group.
 * Route: /join/:code
 */
const JoinGroup = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const { user, fetchWithAuth } = useContext(AuthContext);
    const [groupInfo, setGroupInfo] = useState(null);
    const [status, setStatus] = useState('loading'); // loading|info|consent|done|error
    const [error, setError] = useState('');
    const [consentChecked, setConsentChecked] = useState(false);

    // Step 1: fetch group info (public, no auth required)
    useEffect(() => {
        fetch(`/api/coach/invite/${code}/`)
            .then(r => r.json())
            .then(data => {
                if (data.error) {
                    setError(data.error);
                    setStatus('error');
                } else {
                    setGroupInfo(data);
                    setStatus('info');
                }
            })
            .catch(() => { setError('Verbindungsfehler'); setStatus('error'); });
    }, [code]);

    // Step 2: join
    const handleJoin = async () => {
        if (!user) { navigate(`/auth?redirect=/join/${code}`); return; }
        const res = await fetchWithAuth(`/api/coach/join/${code}/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        if (data.requires_consent) {
            setStatus('consent');
        } else {
            setStatus('done');
        }
    };

    // Step 3: give DSGVO consent
    const handleConsent = async () => {
        const res = await fetchWithAuth(`/api/coach/join/${code}/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ consent: true }),
        });
        if (res.ok) setStatus('done');
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-plantlix-50 to-white dark:from-[#111] dark:to-[#1a1a1a]">
            <div className="w-full max-w-md">
                {status === 'loading' && (
                    <div className="text-center py-16">
                        <div className="w-10 h-10 mx-auto border-4 border-plantlix-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}

                {status === 'error' && (
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 text-center shadow-xl border border-gray-100 dark:border-white/5">
                        <p className="text-5xl mb-4">❌</p>
                        <h2 className="font-bold text-lg text-gray-900 dark:text-white">{error}</h2>
                        <p className="text-sm text-gray-500 mt-2">Dieser Einladungslink ist ungültig oder abgelaufen.</p>
                        <button onClick={() => navigate('/')} className="mt-6 px-6 py-2.5 rounded-xl bg-plantlix-500 text-white text-sm font-bold">
                            Zur Startseite
                        </button>
                    </div>
                )}

                {status === 'info' && groupInfo && (
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 text-center shadow-xl border border-gray-100 dark:border-white/5 space-y-6">
                        <div>
                            <p className="text-5xl mb-3">🌱</p>
                            <h2 className="font-bold text-xl text-gray-900 dark:text-white">Einladung zur Ausbildungsgruppe</h2>
                        </div>
                        <div className="bg-plantlix-50 dark:bg-plantlix-900/10 rounded-xl p-5">
                            <p className="font-bold text-plantlix-700 dark:text-plantlix-400 text-lg">{groupInfo.group_name}</p>
                            {groupInfo.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{groupInfo.description}</p>}
                            <p className="text-xs text-gray-500 mt-3">
                                Ausbilder: {groupInfo.trainer_names.join(', ')}
                            </p>
                        </div>
                        <button
                            onClick={handleJoin}
                            className="w-full py-3 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 text-white font-bold text-sm transition shadow-lg shadow-plantlix-500/20"
                        >
                            {user ? 'Gruppe beitreten' : 'Anmelden & beitreten'}
                        </button>
                    </div>
                )}

                {status === 'consent' && (
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-white/5 space-y-5">
                        <div className="text-center">
                            <p className="text-4xl mb-3">🔒</p>
                            <h2 className="font-bold text-lg text-gray-900 dark:text-white">Datenschutz-Zustimmung</h2>
                            <p className="text-sm text-gray-500 mt-2">Dein Ausbilder kann folgende Daten einsehen:</p>
                        </div>

                        <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-300 space-y-2">
                            <p>• <strong>Lernfortschritt</strong> – Welche Pflanzen gelernt, in Arbeit oder offen sind</p>
                            <p>• <strong>Quiz-Ergebnisse</strong> – Trefferquoten bei zugewiesenen Quizzen</p>
                            <p>• <strong>Berichtshefte</strong> – Deine wöchentlichen Ausbildungsnachweise</p>
                            <p>• <strong>Streak</strong> – Wie regelmäßig du lernst</p>
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={consentChecked}
                                onChange={e => setConsentChecked(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-plantlix-500 focus:ring-plantlix-400"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Ich stimme zu, dass mein Ausbilder die oben genannten Daten einsehen kann.
                                Diese Zustimmung kann jederzeit in den Profileinstellungen widerrufen werden.
                            </span>
                        </label>

                        <button
                            onClick={handleConsent}
                            disabled={!consentChecked}
                            className="w-full py-3 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 disabled:opacity-40 text-white font-bold text-sm transition"
                        >
                            Zustimmen & beitreten
                        </button>
                    </div>
                )}

                {status === 'done' && (
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 text-center shadow-xl border border-gray-100 dark:border-white/5 space-y-4">
                        <p className="text-5xl mb-2">🎉</p>
                        <h2 className="font-bold text-lg text-gray-900 dark:text-white">Willkommen in der Gruppe!</h2>
                        <p className="text-sm text-gray-500">Du bist jetzt Mitglied. Dein Ausbilder kann deinen Lernfortschritt einsehen.</p>
                        <button onClick={() => navigate('/')} className="px-6 py-2.5 rounded-xl bg-plantlix-500 text-white text-sm font-bold transition">
                            Zum Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JoinGroup;
